import { i18n } from "../i18n/i18n";

import React, { Component } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { some } from 'lodash'
import { connect, ConnectedProps } from 'react-redux'
import routes from '../routes/routes'
import confirmService from '../services/confirm.service'
import itemService from '../services/item.service'
import itemApi, { ItemData } from '../apis/item.api'
import workOrderApi, { WorkOrderCreateRequest } from '../apis/workOrder.api'
import { ItemType } from '../apis/ItemType.api'
import {
  resetPagination as resetWorkOrderPagination
} from '../views/WorkOrders/WorkOrders.slice'
import dateUtil from '../utils/date.util'
import { ConfirmParams } from '../components/Confirm/Confirm.state'
import Card from '../components/Card'
import PageHeading from '../components/PageHeading'
import Row from '../components/Row/Row'
import InputText from '../components/InputText'
import SearchPicker, { SearchPickerOption } from '../components/SearchPicker'
import DatePicker from '../components/DatePicker/DatePicker'
import ItemMaterials from './ItemMaterials'
import CardButton from '../components/CardButton'

interface WorkOrderCreateFormRouteState {
  produceAt?: string
}

interface Props
  extends RouteComponentProps<any, any, WorkOrderCreateFormRouteState>, ConnectedProps<typeof connector> {
  onItemSelect: (item: ItemData | null) => void
  onQuantityChange: (quantity: number) => void
}

interface WorkOrderCreateFormState {
  item: string
  itemId: number | null
  line: string
  salesOrder: string
  quantity: string
  produceAt: string
  items: ItemData[]
}

class WorkOrderCreateForm
  extends Component<Props, WorkOrderCreateFormState> {
  constructor (props: Props) {
    super(props)
    const date = dateUtil.now()
    this.state = {
      item: '',
      itemId: null,
      line: '',
      salesOrder: '',
      quantity: '',
      produceAt: date,
      items: []
    }
    this.handleItemSearch = this.handleItemSearch.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleItemSelect = this.handleItemSelect.bind(this)
    this.handleDateChange = this.handleDateChange.bind(this)
    this.handleCreateClick = this.handleCreateClick.bind(this)
    this.handleItemBlur = this.handleItemBlur.bind(this)
  }

  get itemSearchOptions () {
    return itemService.searchOptions(this.state.items)
  }

  get itemMaterials () {
    const { itemId } = this.state
    return itemId &&
      <ItemMaterials id={itemId} quantity={Number(this.state.quantity)} />
  }

  handleItemSearch (name: string, term: string) {
    const types: ItemType[] = ['production']
    itemApi.searchByCode(term, types, items => {
      if (!items) return
      this.setState({ items })
    })
  }

  handleItemBlur () {
    this.setState({ items: [] })
  }

  handleInputChange (name: string, value: string) {
    if (name === 'item') {
      this.setState({ itemId: null, item: value })
      this.props.onItemSelect(null)
    } else if (name === 'quantity') {
      this.setState({ quantity: value }, () => {
        this.props.onQuantityChange(Number(value) || 0)
      })
    } else if (name === 'line') this.setState({ line: value })
    else if (name === 'salesOrder') this.setState({ salesOrder: value })
  }

  handleItemSelect (name: string, option: SearchPickerOption) {
    const { value } = option
    const itemId = option.id as number
    const item = itemService.find(this.state.items, itemId)
    if (!item) return
    this.props.onItemSelect(item)
    this.setState({ items: [], item: value, itemId })
  }

  handleDateChange (name: string, date: string) {
    if (name === 'produceAt') this.setState({ produceAt: date })
  }

  createWorkOrder () {
    const { itemId, line, salesOrder, quantity, produceAt } = this.state
    const payload: WorkOrderCreateRequest = {}
    if (itemId) payload.item = itemId
    if (line) payload.lineNumber = line
    if (salesOrder) payload.salesOrder = salesOrder
    if (quantity) payload.quantity = Number(quantity)
    if (produceAt) payload.produceDate = produceAt
    workOrderApi.create(payload, workOrder => {
      if (!workOrder) return
      this.props.resetWorkOrderPagination()
      this.props.history.push(routes.workOrders)
    })
  }

  confirmMaterialDeficit () {
    const confirm: ConfirmParams = {
      id: 0,
      question: i18n('Please note this work order has a material deficit'),
      confirmText: i18n('Confirm'),
      cancelText: i18n('Cancel'),
      type: 'instruction',
      onConfirm: () => this.createWorkOrder()
    }
    confirmService.push(confirm)
  }

  checkMaterialDeficit () {
    const { itemId, quantity } = this.state
    if (!itemId) return
    itemApi.materialsDelta(itemId, Number(quantity), (materialDelta) => {
      const hasDeficit = some(materialDelta, data => data.delta < 0)
      if (hasDeficit) return this.confirmMaterialDeficit()
      this.createWorkOrder()
    })
  }

  handleCreateClick () {
    const confirm: ConfirmParams = {
      id: 0,
      question: i18n('Please review and confirm the work order details'),
      confirmText: i18n('Confirm'),
      cancelText: i18n('Cancel'),
      onConfirm: () => this.checkMaterialDeficit()
    }
    confirmService.push(confirm)
  }

  setRouteState () {
    const { state: locState } = this.props.location
    if (locState && locState.produceAt) {
      this.setState({ produceAt: locState.produceAt })
    }
  }

  componentDidMount () {
    this.setRouteState()
  }

  render () {
    return (
      <Card>
        <PageHeading
          text={i18n("Create Work Order")}
          doc='work-orders-create-work-order' />
        <Row column={2}>
          <SearchPicker
            name="item"
            value={this.state.item}
            options={this.itemSearchOptions}
            label={i18n("Item code")}
            onSearch={this.handleItemSearch}
            onChange={this.handleInputChange}
            onSelect={this.handleItemSelect}
            onBlur={this.handleItemBlur} />
          <InputText
            name="line"
            value={this.state.line}
            label={i18n("Line number")}
            onChange={this.handleInputChange} />
          <InputText
            name="salesOrder"
            value={this.state.salesOrder}
            label={i18n("Sales order")}
            onChange={this.handleInputChange} />
          <InputText
            name="quantity"
            value={this.state.quantity}
            label={i18n("Quantity")}
            onChange={this.handleInputChange} />
          <DatePicker
            name="produceAt"
            value={this.state.produceAt}
            label={i18n("Production date")}
            onChange={this.handleDateChange} />
        </Row>
        {this.itemMaterials}
        <CardButton
          proceedText={i18n("Create")}
          cancelText={i18n("Cancel")}
          onProceedClick={this.handleCreateClick} />
      </Card>
    )
  }
}

const mapDispatchToProps = {
  resetWorkOrderPagination
}

const connector = connect(null, mapDispatchToProps)
export default connector(withRouter(WorkOrderCreateForm))
